// 매핑 탭 "대기 상태 다운로드" 전용 엑셀 빌더.
// 3개 시트(대기 매핑 / 원본 섹터 / 승인 후 시뮬레이션)를 ExcelJS 로 생성 → Blob 반환.
//
// 권위: dashboard-web 작업 지시 "대기 상태 다운로드" 스펙.
// 테스트 관점에서 buildPendingMappingsWorkbook 은 순수 함수(데이터 → Workbook),
// downloadPendingMappingsXlsx 가 Blob 트리거까지 맡는다.

// ExcelJS 는 로드 가중 큰 패키지 (>900KB) 이므로 지연 import.
// 초기 라우트 chunk 에 포함되지 않도록 함수 내부에서 동적으로 await import('exceljs').
import type ExcelJS from 'exceljs';
import {
  type SectorMappingItem,
  type SectorTaxonomyNode,
} from '../../api/sectorTaxonomyApi';
import { formatTaxonomySource, formatMappingStatus } from './sectorTaxonomy.types';

export interface BuildPendingExcelInput {
  pendingMappings: SectorMappingItem[];
  taxonomy: SectorTaxonomyNode[];
  /**
   * 원본 섹터 시트를 위한 "확정·대기·반려 포함 전체 매핑" 목록.
   * 없으면 pendingMappings 로 대체해서 최소 정보라도 내려준다.
   */
  allMappingsForSourceSheet?: SectorMappingItem[];
  /**
   * 최신 Type-A run 의 uncovered_sources — LLM 이 현재 체계에 편입 못 한 원천.
   * 형식은 "{source}:{source_sector_name}" 또는 단순 "{source_sector_name}"
   * (백엔드가 raw_proposal 에서 그대로 전달). 시트 1/2 에 uncovered 행으로 포함.
   */
  uncoveredSources?: string[];
}

/**
 * uncovered 문자열 → (source, name) 파싱.
 * - "naver_primary:XYZ" → {source:'naver_primary', name:'XYZ'}
 * - "naver_theme:ABC"   → {source:'naver_theme',   name:'ABC'}
 * - "기타"              → {source:null, name:'기타'}
 */
function parseUncovered(raw: string): { source: string | null; name: string } {
  const m = /^(naver_primary|naver_theme):(.+)$/.exec(raw);
  if (m) return { source: m[1], name: m[2] };
  return { source: null, name: raw };
}

// ── 내부 유틸 ────────────────────────────────────────────────────

function min(xs: number[]): number | null {
  return xs.length === 0 ? null : xs.reduce((a, b) => (a < b ? a : b));
}
function max(xs: number[]): number | null {
  return xs.length === 0 ? null : xs.reduce((a, b) => (a > b ? a : b));
}
function avg(xs: number[]): number | null {
  if (xs.length === 0) return null;
  const s = xs.reduce((a, b) => a + b, 0);
  return s / xs.length;
}

function fmtIso(s: string | null | undefined): string {
  if (!s) return '';
  // 그대로 기록 — 엑셀은 문자열 timestamp 허용. 날짜 변환은 운영자가 엑셀에서 수행.
  return s;
}

// ── 메인 빌더 ────────────────────────────────────────────────────

export async function buildPendingMappingsWorkbook(
  input: BuildPendingExcelInput
): Promise<ExcelJS.Workbook> {
  const { pendingMappings, taxonomy } = input;
  const allForSource = input.allMappingsForSourceSheet ?? pendingMappings;
  const uncoveredRaw = input.uncoveredSources ?? [];
  // uncovered 정규화 + pending 매핑에 이미 포함된 원천은 제외 (중복 방지).
  const pendingKeys = new Set<string>(
    pendingMappings.map((m) => `${m.source}::${m.sourceSectorName}`)
  );
  const uncovered = uncoveredRaw
    .map(parseUncovered)
    .filter((u) => {
      if (!u.source) return true; // source 파싱 실패는 그대로 보존 (식별용)
      return !pendingKeys.has(`${u.source}::${u.name}`);
    });

  // 지연 import — vite 가 exceljs 를 별도 chunk 로 분리한다.
  const ExcelJSMod = await import('exceljs');
  const ExcelJSApi = (ExcelJSMod as unknown as { default: typeof ExcelJS }).default ?? ExcelJSMod;
  const wb = new (ExcelJSApi as typeof ExcelJS).Workbook();
  wb.creator = 'Alpha Omega Quant — Sector Taxonomy';
  wb.created = new Date();

  // ── 시트 1: 대기 매핑 ───────────────────────────────
  const s1 = wb.addWorksheet('대기 매핑');
  s1.columns = [
    { header: 'mapping_id', key: 'mapping_id', width: 12 },
    { header: 'source', key: 'source', width: 14 },
    { header: 'source_sector_name', key: 'source_sector_name', width: 28 },
    { header: 'canonical_sector_id', key: 'canonical_sector_id', width: 24 },
    { header: 'canonical_display_name', key: 'canonical_display_name', width: 20 },
    { header: 'weight', key: 'weight', width: 8 },
    { header: 'merge_strategy', key: 'merge_strategy', width: 14 },
    { header: 'relation', key: 'relation', width: 22 },
    { header: 'overlap_ratio', key: 'overlap_ratio', width: 12 },
    { header: 'llm_confidence', key: 'llm_confidence', width: 14 },
    { header: 'llm_model', key: 'llm_model', width: 20 },
    { header: 'mapping_reason', key: 'mapping_reason', width: 40 },
    { header: 'status', key: 'status', width: 14 },
    { header: 'created_at', key: 'created_at', width: 22 },
  ];
  for (const m of pendingMappings) {
    s1.addRow({
      mapping_id: m.mappingId,
      source: formatTaxonomySource(m.source),
      source_sector_name: m.sourceSectorName,
      canonical_sector_id: m.canonicalSectorId,
      canonical_display_name: m.canonicalDisplayName,
      weight: m.weight,
      merge_strategy: m.mergeStrategy ?? '',
      relation: m.relation ?? '',
      overlap_ratio: m.overlapRatio,
      llm_confidence: m.llmConfidence,
      llm_model: m.llmModel ?? '',
      mapping_reason: m.mappingReason ?? '',
      status: formatMappingStatus(m.status),
      created_at: fmtIso(m.createdAt),
    });
  }
  // uncovered 원천을 "매핑 없음" 행으로 추가 — 원천 342 완전 커버.
  for (const u of uncovered) {
    s1.addRow({
      mapping_id: '',
      source: u.source ? formatTaxonomySource(u.source) : '—',
      source_sector_name: u.name,
      canonical_sector_id: '',
      canonical_display_name: '',
      weight: '',
      merge_strategy: '',
      relation: '',
      overlap_ratio: '',
      llm_confidence: '',
      llm_model: '',
      mapping_reason: 'LLM 이 이번 체계에 편입하지 않음 (uncovered)',
      status: 'uncovered',
      created_at: '',
    });
  }
  s1.getRow(1).font = { bold: true };
  s1.autoFilter = { from: 'A1', to: { row: 1, column: s1.columns.length } };
  s1.views = [{ state: 'frozen', ySplit: 1 }];

  // ── 시트 2: 원본 섹터 ───────────────────────────────
  // (source, source_sector_name) distinct + 집계.
  // 매핑 건수 / has_pending 플래그는 allForSource (가능하면 전체) 기반.
  type SourceKey = string; // `${source}::${name}`
  interface SourceAgg {
    source: string;
    name: string;
    mappingCount: number;
    hasPending: boolean;
  }
  const sourceMap = new Map<SourceKey, SourceAgg>();
  for (const m of allForSource) {
    const key: SourceKey = `${m.source}::${m.sourceSectorName}`;
    const prev = sourceMap.get(key);
    if (prev) {
      prev.mappingCount += 1;
      if (m.status === 'pending_review') prev.hasPending = true;
    } else {
      sourceMap.set(key, {
        source: m.source,
        name: m.sourceSectorName,
        mappingCount: 1,
        hasPending: m.status === 'pending_review',
      });
    }
  }
  // pendingMappings 에는 있지만 allForSource 에 없을 수도 있으므로 보강.
  for (const m of pendingMappings) {
    const key: SourceKey = `${m.source}::${m.sourceSectorName}`;
    if (!sourceMap.has(key)) {
      sourceMap.set(key, {
        source: m.source,
        name: m.sourceSectorName,
        mappingCount: 1,
        hasPending: true,
      });
    } else {
      sourceMap.get(key)!.hasPending = true;
    }
  }

  const s2 = wb.addWorksheet('원본 섹터');
  s2.columns = [
    { header: 'source', key: 'source', width: 16 },
    { header: 'source_sector_name', key: 'source_sector_name', width: 32 },
    { header: 'mapping_count', key: 'mapping_count', width: 14 },
    { header: 'has_pending', key: 'has_pending', width: 12 },
    // status: 'included' (이번 체계 편입) | 'uncovered' (LLM 이 편입하지 않음) | 'legacy' (과거 run 흔적만).
    { header: 'status', key: 'status', width: 14 },
  ];
  const sortedSources = Array.from(sourceMap.values()).sort((a, b) =>
    a.source === b.source ? a.name.localeCompare(b.name) : a.source.localeCompare(b.source)
  );
  for (const row of sortedSources) {
    s2.addRow({
      source: formatTaxonomySource(row.source),
      source_sector_name: row.name,
      mapping_count: row.mappingCount,
      has_pending: row.hasPending ? 'Y' : 'N',
      status: row.hasPending ? 'included' : 'legacy',
    });
  }
  // uncovered 원천 추가 행 — 원천 342 완전 커버.
  for (const u of uncovered) {
    s2.addRow({
      source: u.source ? formatTaxonomySource(u.source) : '—',
      source_sector_name: u.name,
      mapping_count: 0,
      has_pending: 'N',
      status: 'uncovered',
    });
  }
  s2.getRow(1).font = { bold: true };
  s2.autoFilter = { from: 'A1', to: { row: 1, column: s2.columns.length } };
  s2.views = [{ state: 'frozen', ySplit: 1 }];

  // ── 시트 3: 승인 후 시뮬레이션 ───────────────────────
  // pendingByCanonical groupBy → canonical 별 집계.
  const pendingByCanonical = new Map<string, SectorMappingItem[]>();
  for (const m of pendingMappings) {
    const list = pendingByCanonical.get(m.canonicalSectorId) ?? [];
    list.push(m);
    pendingByCanonical.set(m.canonicalSectorId, list);
  }

  const s3 = wb.addWorksheet('승인 후 시뮬레이션');
  s3.columns = [
    { header: 'canonical_sector_id', key: 'canonical_sector_id', width: 24 },
    { header: 'display_name', key: 'display_name', width: 20 },
    { header: 'total_confirmed', key: 'total_confirmed', width: 16 },
    { header: 'total_pending', key: 'total_pending', width: 16 },
    { header: 'total_after_approval', key: 'total_after_approval', width: 20 },
    { header: 'pending_min_confidence', key: 'pending_min_confidence', width: 22 },
    { header: 'pending_avg_confidence', key: 'pending_avg_confidence', width: 22 },
    { header: 'pending_max_confidence', key: 'pending_max_confidence', width: 22 },
    { header: 'sibling_overlap_count', key: 'sibling_overlap_count', width: 20 },
    { header: 'avg_overlap_ratio', key: 'avg_overlap_ratio', width: 18 },
    { header: 'max_overlap_ratio', key: 'max_overlap_ratio', width: 18 },
    { header: 'pending_sources_preview', key: 'pending_sources_preview', width: 50 },
  ];

  // taxonomy 에 없는 canonical 이 pending 에 있을 수도 있으므로 union.
  // 단 매핑 0 인 anchor L1 (sec_l1_auto_mobility 등 메타 부모) 은 운영자 검토 대상 외 — 제외.
  // confirmed 든 pending 이든 매핑이 1개라도 있는 canonical 만 노출.
  const taxonomyById = new Map<string, SectorTaxonomyNode>();
  for (const t of taxonomy) taxonomyById.set(t.canonicalSectorId, t);
  const allCanonicalIds = new Set<string>([
    ...taxonomy
      .filter((t) => (t.mappingCount ?? 0) > 0)
      .map((t) => t.canonicalSectorId),
    ...pendingByCanonical.keys(),
  ]);

  const sortedCanonicalIds = Array.from(allCanonicalIds).sort();
  for (const cid of sortedCanonicalIds) {
    const node = taxonomyById.get(cid);
    const pendingList = pendingByCanonical.get(cid) ?? [];
    const confidences = pendingList
      .map((p) => p.llmConfidence)
      .filter((c): c is number => c !== null && c !== undefined);
    // relation === 'sibling_by_overlap' 인 매핑의 Jaccard 통계 — 머지 근거 강도 요약.
    const overlaps = pendingList
      .filter((p) => p.relation === 'sibling_by_overlap' && p.overlapRatio != null)
      .map((p) => p.overlapRatio as number);
    // 최대 20개까지 표시 (이전 5개 → 확장). 한 canonical 에 N:1 머지된 원천
    // 전체를 운영자가 직접 확인 가능. 20초과는 "외 N" 로 뭉친다.
    const PREVIEW_MAX = 20;
    const preview = pendingList
      .slice(0, PREVIEW_MAX)
      .map((p) => `${p.source}:${p.sourceSectorName}`);
    const overflow = pendingList.length > PREVIEW_MAX
      ? ` 외 ${pendingList.length - PREVIEW_MAX}`
      : '';
    const confirmedCount =
      node?.confirmedMappingCount ?? node?.mappingCount ?? 0;

    s3.addRow({
      canonical_sector_id: cid,
      display_name: node?.displayName ?? '(taxonomy 미등록)',
      total_confirmed: confirmedCount,
      total_pending: pendingList.length,
      total_after_approval: confirmedCount + pendingList.length,
      pending_min_confidence: min(confidences),
      pending_avg_confidence: avg(confidences),
      pending_max_confidence: max(confidences),
      sibling_overlap_count: overlaps.length,
      avg_overlap_ratio: avg(overlaps),
      max_overlap_ratio: max(overlaps),
      pending_sources_preview: preview.join(';') + overflow,
    });
  }
  s3.getRow(1).font = { bold: true };
  s3.autoFilter = { from: 'A1', to: { row: 1, column: s3.columns.length } };
  s3.views = [{ state: 'frozen', ySplit: 1 }];

  // 신뢰도 + overlap 소수점 2자리 포맷 (운영자 가독성).
  // F/G/H = pending_min/avg/max_confidence, J/K = avg/max_overlap_ratio.
  ['F', 'G', 'H', 'J', 'K'].forEach((col) => {
    s3.getColumn(col).numFmt = '0.00';
  });
  s1.getColumn('llm_confidence').numFmt = '0.00';
  s1.getColumn('overlap_ratio').numFmt = '0.00';

  return wb;
}

// ── 파일명 ───────────────────────────────────────────────────────
export function buildPendingExcelFilename(now: Date = new Date()): string {
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const mi = String(now.getMinutes()).padStart(2, '0');
  return `sector-taxonomy-pending-${yyyy}-${mm}-${dd}_${hh}${mi}.xlsx`;
}

// ── 다운로드 트리거 ────────────────────────────────────────────────
export async function downloadPendingMappingsXlsx(
  input: BuildPendingExcelInput,
  now: Date = new Date()
): Promise<void> {
  const wb = await buildPendingMappingsWorkbook(input);
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = buildPendingExcelFilename(now);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // 약간의 지연 후 해제 — 일부 브라우저가 즉시 revoke 시 다운로드 취소하는 이슈 회피.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
